import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {map, mergeMap, of, switchMap, tap} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {OrganizationActions} from '../organization/organization.actions';
import {PatronService} from './patron.service';
import {PatronActions} from './patron.actions';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';
import {whenRouterHasNavigatedTo, whenRouterHasNavigatedToAChildOf, withLatestFromSelectors} from '@store/common/effects.helpers';
import {ChangeEmailDialog} from '@app/my-account/my-profile/dialogs/change-email.dialog';
import {CardLoginValue, ChangePatronEmailValue, EmailLoginValue} from '@store/patron/patron.types';
import {openFormDialog, openPersistentFormDialog} from '@aviary/store/common/effect-dialog.helpers';
import {Store} from '@ngrx/store';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {LoginDialog} from '@app/index/login/login.dialog';
import {isCardLogin, isIncorrectCurrentPinFailure, isPinChangeLockedFailure} from '@store/patron/patron.helpers';
import {RouterActions, RouterSelectors, SnackBarActions} from '@store/aviary/aviary.adapter';
import {HoldsActions} from '@store/holds/holds.actions';
import {BalanceActions} from '@store/balance/balance.actions';
import {ItemRequestsActions} from '@store/item-requests/item-requests.actions';
import {AuthService} from '@aviary/services/auth.service';
import {BalanceSelectors} from '@store/balance/balance.selectors';
import {ChangePasswordDialog, ChangePasswordValue} from '@app/my-account/my-profile/dialogs/change-password.dialog';
import {isIncorrectCurrentPasswordFailure} from '@aviary/services/auth.helpers';
import {ChangePinDialog, ChangePinValue} from '@app/my-account/my-profile/dialogs/change-pin.dialog';

@Injectable({providedIn: 'root'})
export class PatronEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly authService: AuthService = inject(AuthService);
  private readonly service: PatronService = inject(PatronService);
  private readonly store: Store = inject(Store);

  public loadAuthTokenOnLoadOrgSuccess = createEffect(() =>
    this.actions.pipe(
      ofType(OrganizationActions.loadAuthOrganizationSuccess),
      tap(({organization}) => this.authService.configureTenantId(organization.googlePatronTenantId)),
      map(() => PatronActions.initializeAuthToken())
    )
  );

  public initializeAuthToken = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.initializeAuthToken, PatronActions.loginPatronSuccess),
      switchMap(() => this.authService.getAsyncToken().pipe(
        map((response: string | null) => isPopulatedString(response) ?
          PatronActions.initializeAuthTokenSuccess(response) :
          PatronActions.initializeAuthTokenNoAuth()
        ),
        catchError((error: unknown) => of(PatronActions.initializeAuthTokenFailure(error)))
      ))
    )
  );


  public loadPatronOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('MY_PROFILE', 'SETTINGS'),
      map(() => PatronActions.reloadAuthPatron())
    )
  );

  public loadAuthPatron = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.initializeAuthTokenSuccess, PatronActions.reloadAuthPatron),
      switchMap(() => this.service.loadAuthPatron().pipe(
        mergeMap(response => [
          PatronActions.loadAuthPatronSuccess(response),
          PatronActions.loadPatronReferenceOptions()
        ]),
        catchError((error: unknown) => of(PatronActions.loadAuthPatronFailure(error)))
      ))
    )
  );

  public loadPatronReferenceOptions = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadPatronReferenceOptions),
      switchMap(() => this.service.loadPatronReferenceOptions().pipe(
        map(response => PatronActions.loadPatronReferenceOptionsSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadPatronReferenceOptionsFailure(error)))
      ))
    )
  );

  public showLoginDialog = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.showLoginDialog),
      openFormDialog(({recordToPlaceHold}) => ({
        componentClass: LoginDialog,
        data: {},
        onConfirm: (formValue: EmailLoginValue | CardLoginValue) => isCardLogin(formValue)
          ? PatronActions.loginPatronWithCard(formValue.cardNumber, formValue.pin, recordToPlaceHold)
          : PatronActions.loginPatronWithEmail(formValue.email, formValue.password, recordToPlaceHold)
      }))
    )
  );

  public loginPatronWithEmail = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loginPatronWithEmail),
      withLatestFromSelectors(RouterSelectors.routeName),
      switchMap(([{email, password, recordToPlaceHold}, routeName]) => this.authService.login(email, password).pipe(
        mergeMap(() => [
          PatronActions.loginPatronSuccess(),
          ...routeName === 'INDEX' ? [RouterActions.navigateTo('DASHBOARD')] : [],
          ...recordToPlaceHold ? [HoldsActions.preparePlaceHold(recordToPlaceHold)] : []
        ]),
        catchError((error: unknown) => of(PatronActions.loginPatronFailure(error)))
      ))
    )
  );

  public loginPatronWithCard = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loginPatronWithCard),
      withLatestFromSelectors(RouterSelectors.routeName),
      switchMap(([{cardNumber, pin, recordToPlaceHold}, routeName]) => this.service.lookupAccount(cardNumber, pin).pipe(
        switchMap(({loginToken}) => this.authService.loginWithToken(loginToken).pipe(
          mergeMap(() => [
            PatronActions.loginPatronSuccess(),
            ...routeName === 'INDEX' ? [RouterActions.navigateTo('DASHBOARD')] : [],
            ...recordToPlaceHold ? [HoldsActions.preparePlaceHold(recordToPlaceHold)] : []
          ]),
          catchError((error: unknown) => of(PatronActions.loginPatronFailure(error)))
        )),
        catchError((error: unknown) => of(PatronActions.loginPatronFailure(error)))
      ))
    )
  );

  public loginError = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loginPatronFailure),
      map(() => SnackBarActions.showError('Login failed. Please try again.'))
    )
  );

  public logoutPatron = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.logoutPatron),
      switchMap(() => this.authService.logout().pipe(
        tap(() => window.location.href = '/')
      ))
    ), {dispatch: false}
  );

  public initializeAccountStatus = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.initializeAuthTokenSuccess, PatronActions.reloadAuthPatron),
      map(() => PatronActions.loadAccountStatus()))
  );

  public reloadAccountStatus = createEffect(() =>
    this.actions.pipe(
      ofType(
        BalanceActions.submitMakeAPaymentSuccess,
        HoldsActions.placeHoldSuccess,
        HoldsActions.editHoldSuccess,
        HoldsActions.cancelHoldSuccess,
        ItemRequestsActions.createItemRequestSuccess,
      ),
      map(() => PatronActions.loadAccountStatus()))
  );

  public loadAccountStatus = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadAccountStatus),
      switchMap(() => this.service.loadAccountStatus().pipe(
        map(response => PatronActions.loadAccountStatusSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadAccountStatusFailure(error)))
      ))
    )
  );

  public editPatron = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.editPatron),
      switchMap(({formValue}) => this.service.updatePatron(formValue).pipe(
        mergeMap(response => [
          PatronActions.editPatronSuccess(response),
          SnackBarActions.showSuccess('Changes successfully saved.'),
          PatronActions.reloadAuthPatron(),
        ]),
        catchError((error: unknown) => of(PatronActions.editPatronFailure(error)))
      ))
    )
  );

  public showChangePatronEmail = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.showChangePatronEmail),
      withLatestFromSelectors(PatronSelectors.Auth.email),
      openFormDialog(([, email]) => ({
        componentClass: ChangeEmailDialog,
        data: {email},
        onConfirm: (formValue: ChangePatronEmailValue) => PatronActions.changePatronEmail(formValue.email)
      }))
    )
  );

  public showChangePassword = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.showChangePassword),
      openPersistentFormDialog(() => ({
        componentClass: ChangePasswordDialog,
        data: {},
        onConfirm: (formValue: ChangePasswordValue) => this.authService.reauthenticateAndChangePassword(formValue.currentPassword, formValue.newPassword),
        onSuccess: () => [PatronActions.changePasswordSuccess(), SnackBarActions.showSuccess('Password successfully updated.')],
        onFailure: (error: unknown) => isIncorrectCurrentPasswordFailure(error) ? [] : PatronActions.changePasswordFailure(error)
      }))
    )
  );

  public changePasswordFailure = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.changePasswordFailure),
      map(() => SnackBarActions.showError('Password could not be updated. Please try again.'))
    )
  );

  public showChangePin = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.showChangePin),
      openPersistentFormDialog(() => ({
        componentClass: ChangePinDialog,
        data: {},
        onConfirm: (formValue: ChangePinValue) => this.service.changePin(formValue.currentPin, formValue.newPin),
        onSuccess: () => [PatronActions.changePinSuccess(), SnackBarActions.showSuccess('PIN successfully updated.')],
        onFailure: (error: unknown) => isIncorrectCurrentPinFailure(error) ? [] : PatronActions.changePinFailure(error)
      }))
    )
  );

  public changePinFailure = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.changePinFailure),
      map(({error}) => SnackBarActions.showError(
        isPinChangeLockedFailure(error)
          ? 'PIN changes are locked. Contact library staff for assistance.'
          : 'PIN could not be updated. Please try again.'
      ))
    )
  );

  public changePatronEmail = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.changePatronEmail),
      switchMap(({email}) => this.service.changePatronEmail(email).pipe(
        map(() => PatronActions.reloadAuthPatron()),
        catchError((error: unknown) => of(PatronActions.changePatronEmailFailure(error)))
      ))
    )
  );

  public loadNotificationPreferencesOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('SETTINGS'),
      map(() => PatronActions.loadNotificationPreferences())
    )
  );

  public loadPatronNotificationPreferences = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadNotificationPreferences),
      switchMap(() => this.service.loadNotificationPreferences().pipe(
        map(response => PatronActions.loadNotificationPreferencesSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadNotificationPreferencesFailure(error)))
      ))
    )
  );

  public editPatronNotificationPreferences = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.editNotificationPreferences),
      concatLatestFrom(() => [
        this.store.select(PatronSelectors.NotificationPreferences.notifications),
        this.store.select(PatronSelectors.Auth.id)
      ]),
      switchMap(([{formValue}, oldPreferences, patronId]) => this.service.updateNotificationPreferences(formValue, oldPreferences, patronId).pipe(
        mergeMap(response => [
          PatronActions.editNotificationPreferencesSuccess(response),
          SnackBarActions.showSuccess('Changes successfully saved.')
        ]),
        catchError((error: unknown) => of(PatronActions.editNotificationPreferencesFailure(error)))
      ))
    )
  );

  public loadPatronRelationshipsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('RELATED_ACCOUNTS'),
      map(() => PatronActions.loadPatronRelationships())
    )
  );

  public loadPatronRelationships = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadPatronRelationships),
      switchMap(() => this.service.loadPatronRelationships().pipe(
        map(response => PatronActions.loadPatronRelationshipsSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadPatronRelationshipsFailure(error)))
      ))
    )
  );

  public updatePatronQuickToggles = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.updatePatronQuickToggles),
      withLatestFromSelectors(PatronSelectors.Auth.myProfileEditValue),
      switchMap(([{value}, currentProfileValue]) => this.service.updatePatronQuickToggles(value, currentProfileValue).pipe(
        mergeMap(response => [
          PatronActions.editPatronSuccess(response),
          SnackBarActions.showSuccess('Changes successfully saved.'),
          PatronActions.reloadAuthPatron(),
        ]),
        catchError((error: unknown) => of(PatronActions.editPatronFailure(error)))
      ))
    )
  );


  //////////////////
  // Dependents ////
  //////////////////
  public redirectToChildTabRouteOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('DEPENDENT_DETAILS'),
      withLatestFromSelectors(RouterSelectors.params),
      map(([, currentParams]) => RouterActions.replaceRouteWith('DEPENDENT_CHECKED_OUT', currentParams))
    )
  );

  // Dependent Details
  public loadDependentDetailsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedToAChildOf('DEPENDENT_DETAILS'),
      withLatestFromSelectors(RouterSelectors.paramValueAsNumberWithFallback('id', 0)),
      map(([, dependentId]) => PatronActions.loadDependentDetails(dependentId))
    )
  );

  public loadDependentDetails = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadDependentDetails),
      switchMap(({dependentId}) => this.service.loadDependentDetails(dependentId).pipe(
        map(response => PatronActions.loadDependentDetailsSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadDependentDetailsFailure(error)))
      ))
    )
  );

  // Dependent Checkouts
  public loadDependentCheckoutsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('DEPENDENT_CHECKED_OUT'),
      withLatestFromSelectors(RouterSelectors.paramValueAsNumberWithFallback('id', 0)),
      map(([, dependentId]) => PatronActions.loadDependentCheckouts(dependentId))
    )
  );

  public loadDependentCheckouts = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadDependentCheckouts),
      withLatestFromSelectors(RouterSelectors.queryParams),
      switchMap(([{dependentId}, queryParams]) => this.service.loadDependentCheckouts(dependentId, queryParams).pipe(
        map(response => PatronActions.loadDependentCheckoutsSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadDependentCheckoutsFailure(error)))
      ))
    )
  );


  // Dependent Holds
  public loadAllDependentHoldsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('DEPENDENT_HOLDS'),
      withLatestFromSelectors(RouterSelectors.paramValueAsNumberWithFallback('id', 0)),
      mergeMap(([, dependentId]) => [
        PatronActions.loadDependentReadyForPickupHolds(dependentId),
        PatronActions.loadDependentOnHoldHolds(dependentId)
      ])
    )
  );

  public loadDependentReadyForPickupHolds = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadDependentReadyForPickupHolds),
      switchMap(({dependentId}) => this.service.loadDependentReadyForPickupHolds(dependentId).pipe(
        map(response => PatronActions.loadDependentReadyForPickupHoldsSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadDependentReadyForPickupHoldsFailure(error)))
      ))
    )
  );

  public loadDependentOnHoldHolds = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadDependentOnHoldHolds),
      withLatestFromSelectors(RouterSelectors.queryParams),
      switchMap(([{dependentId}, queryParams]) => this.service.loadDependentOnHoldHolds(dependentId, queryParams).pipe(
        map(response => PatronActions.loadDependentOnHoldHoldsSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadDependentOnHoldHoldsFailure(error)))
      ))
    )
  );


  // Dependent Balance Info
  public loadAllDependentBalanceInfoOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('DEPENDENT_BALANCE'),
      withLatestFromSelectors(RouterSelectors.paramValueAsNumberWithFallback('id', 0)),
      mergeMap(([, dependentId]) => [
        PatronActions.loadDependentAccountStatus(dependentId),
        PatronActions.loadDependentBalanceTransactions(dependentId),
        BalanceActions.loadSquareAppDetails()
      ])
    )
  );

  public reloadDependentBalanceInfo = createEffect(() =>
    this.actions.pipe(
      ofType(BalanceActions.submitMakeAPaymentForDependentSuccess),
      withLatestFromSelectors(RouterSelectors.paramValueAsNumberWithFallback('id', 0)),
      mergeMap(([, dependentId]) => [
        PatronActions.loadDependentAccountStatus(dependentId),
        PatronActions.loadDependentBalanceTransactions(dependentId)
      ])
    )
  );

  public loadDependentAccountStatus = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadDependentAccountStatus),
      switchMap(({dependentId}) => this.service.loadDependentAccountStatus(dependentId).pipe(
        map(response => PatronActions.loadDependentAccountStatusSuccess(response)),
        catchError((error: unknown) => of(PatronActions.loadDependentAccountStatusFailure(error)))
      ))
    )
  );

  public loadDependentBalanceTransactions = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loadDependentBalanceTransactions),
      withLatestFromSelectors(
        RouterSelectors.queryParams,
        BalanceSelectors.DependentBalanceTransactions.includeAllTransactions
      ),
      switchMap(([{dependentId}, queryParams, includeAllTransactions]) =>
        this.service.loadDependentBalanceTransactions(dependentId, queryParams, includeAllTransactions).pipe(
          map(response => PatronActions.loadDependentBalanceTransactionsSuccess(response)),
          catchError((error: unknown) => of(PatronActions.loadDependentBalanceTransactionsFailure(error)))
        )
      )
    )
  );

  public loadDependentBalanceTransactionsOnToggleIncludeAll = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.toggleIncludeAllDependentBalanceTransactions),
      withLatestFromSelectors(RouterSelectors.paramValueAsNumberWithFallback('id', 0)),
      map(([, dependentId]) => PatronActions.loadDependentBalanceTransactions(dependentId))
    )
  );
}
